import {Component, OnDestroy, OnInit, ViewChild, computed, inject} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {filter, map, switchMap} from 'rxjs/operators';
import {AppMatAutocompleteComponent} from '@xmedical/ui';
import {TreatmentPlanItemAddCardComponent} from '@xmedical/clinical-shared';
import {MatDialog} from '@angular/material/dialog';
import {CoreService} from '../../../core/services/core.service';
import {Patient, TreatmentPlanItem, TreatmentPlansRepositoryService, UserSettings} from '@xmedical/core';
import {PatientStore, PatientTreatmentPlanItemsStore, PatientTreatmentPlansStore} from '../../../core/state';

@Component({
    selector: 'app-patient-treatment-plan-actions-widget',
    templateUrl: './treatment-plan-actions-widget.component.html',
    styleUrls: ['./treatment-plan-actions-widget.component.scss'],
    standalone: false
})
export class TreatmentPlanActionsWidgetComponent implements OnInit, OnDestroy {

  error = '';
  settings: UserSettings;
  resultComments: { [key: number]: string } = {};
  @ViewChild(AppMatAutocompleteComponent) autocomplete: AppMatAutocompleteComponent;
  private subscriptions: Subscription[] = [];

  private patientStore = inject(PatientStore);
  protected itemsStore = inject(PatientTreatmentPlanItemsStore);
  private plansStore = inject(PatientTreatmentPlansStore);

  readonly actions = computed(() => this.itemsStore.items());
  readonly loading = computed(() => this.itemsStore.loading());

  constructor(
    private matDialog: MatDialog,
    private treatmentPlanItemsRepository: TreatmentPlansRepositoryService,
    private coreService: CoreService
  ) {
  }

  get hasError(): boolean {
    return this.error.length > 0;
  }

  get hasItems(): boolean {
    return this.actions().length > 0;
  }

  get patient(): Patient | null {
    return this.patientStore.current();
  }

  get confirmationMessage(): string {
    if (!this.patient) {
      return 'Are you sure you want to save a treatment plan with these actions?';
    }
    return `Are you sure you want to save a treatment plan with these actions for ${this.patient.firstName} ${this.patient.surname}?`;
  }

  searchTreatmentPlanItems$ = (query: string): Observable<TreatmentPlanItem[]> => {
    return this.treatmentPlanItemsRepository.query(query)
      .pipe(map(response => response));
  }

  ngOnInit(): void {
    this.settings = this.coreService.settings();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  onAddNewItem() {
    const modalRef = this.matDialog.open(TreatmentPlanItemAddCardComponent);
    // Card returns form value (no id). Persist to the master list first,
    // then search the autocomplete by the canonical action string so the
    // user can pick the freshly-saved entry.
    this.subscriptions.push(modalRef.afterClosed().pipe(
      filter((v): v is TreatmentPlanItem => !!v),
      switchMap(item => this.treatmentPlanItemsRepository.createTreatmentPlanItem(item).pipe(
        map(() => item),
      )),
    ).subscribe(item => this.autocomplete.search(item.action)));
  }

  onItemSelected(treatmentPlanItem: TreatmentPlanItem) {
    treatmentPlanItem.comment = this.resultComments[treatmentPlanItem.id] || '';
    treatmentPlanItem.createdUser = this.coreService.currentUser;
    treatmentPlanItem.lastModifiedUser = this.coreService.currentUser;
    treatmentPlanItem.createdDate = new Date();
    treatmentPlanItem.createdDateUtc = new Date();
    treatmentPlanItem.lastModifiedDate = new Date();
    treatmentPlanItem.modifiedDateUtc = new Date();
    this.error = '';
    this.subscriptions.push(this.itemsStore.add(treatmentPlanItem).subscribe({
      error: err => {
        this.error = typeof err === 'string' ? err : 'Could not add treatment plan item';
      }
    }));
    this.resultComments = {};
    this.autocomplete.cancelSearch();
  }

  savePlan() {
    this.subscriptions.push(this.plansStore.createFromItems(this.actions()).subscribe({
      error: error => console.error(error)
    }));
  }
}
